/**
 * The difference between two JSON values, and applying it.
 *
 * The server sends a game's state as the changes since the last state that
 * socket was sent (see GameStateUpdatedMessage); the apps rebuild the game by
 * applying them. Both sides only ever handle plain JSON (what came out of
 * JSON.parse): objects, arrays, strings, numbers, booleans and null.
 */
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
function diffInto(prev, next, path, ops) {
    if (prev === next)
        return;
    if (isObject(prev) && isObject(next)) {
        for (const key of Object.keys(prev)) {
            if (!has(next, key))
                ops.push({ op: 'del', path: [...path, key] });
        }
        for (const key of Object.keys(next)) {
            if (has(prev, key))
                diffInto(prev[key], next[key], [...path, key], ops);
            else
                ops.push({ op: 'set', path: [...path, key], value: next[key] });
        }
        return;
    }
    if (Array.isArray(prev) && Array.isArray(next)) {
        const common = Math.min(prev.length, next.length);
        for (let i = 0; i < common; i++)
            diffInto(prev[i], next[i], [...path, i], ops);
        if (next.length < prev.length)
            ops.push({ op: 'len', path, length: next.length });
        for (let i = common; i < next.length; i++)
            ops.push({ op: 'set', path: [...path, i], value: next[i] });
        return;
    }
    ops.push({ op: 'set', path, value: next });
}
/** The changes that turn `prev` into `next`. Neither is modified. */
export function diffJson(prev, next) {
    const ops = [];
    diffInto(prev, next, [], ops);
    return ops;
}
// Keys that would reach an object's prototype instead of the object.
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
function isContainer(value) {
    return typeof value === 'object' && value !== null;
}
/** The object or array at `path`. Throws when there isn't one. */
function containerAt(doc, path) {
    let node = doc;
    for (const key of path) {
        if (!isContainer(node) || UNSAFE_KEYS.has(String(key)))
            throw new Error(`Patch path not found: ${path.join('.')}`);
        node = node[key];
    }
    if (!isContainer(node))
        throw new Error(`Patch path not found: ${path.join('.')}`);
    return node;
}
/**
 * Apply `patch` to `doc` in place and return the result (a new value when the
 * patch replaces the whole of it). Throws when the patch doesn't fit `doc`,
 * which means `doc` isn't the value the patch was made from.
 */
export function applyJsonPatch(doc, patch) {
    let root = doc;
    for (const op of patch) {
        if (op.op === 'len') {
            const target = containerAt(root, op.path);
            if (!Array.isArray(target))
                throw new Error(`Not an array: ${op.path.join('.')}`);
            target.length = op.length;
            continue;
        }
        if (op.path.length === 0) {
            if (op.op !== 'set')
                throw new Error('Cannot delete the whole value');
            root = op.value;
            continue;
        }
        const parent = containerAt(root, op.path.slice(0, -1));
        const key = op.path[op.path.length - 1];
        if (UNSAFE_KEYS.has(String(key)))
            throw new Error(`Patch path not found: ${op.path.join('.')}`);
        if (op.op === 'del')
            delete parent[key];
        else
            parent[key] = op.value;
    }
    return root;
}
